import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiGlobe } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const SEGMENT_COLORS = {
  Domestic: { fill: '#3B82F6', bg: 'tw-bg-blue-50',    text: 'tw-text-blue-700',    badge: 'tw-bg-blue-500'    },
  Oversea:  { fill: '#10B981', bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-700', badge: 'tw-bg-emerald-500' },
  Other:    { fill: '#F59E0B', bg: 'tw-bg-amber-50',   text: 'tw-text-amber-700',   badge: 'tw-bg-amber-500'   }
};

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Annulus geometry matches the previous SVG donut: R=55, stroke=22 → 44…66px.
const buildDonutOption = (items, total) => ({
  textStyle: { fontFamily: CHART_FONT.fontFamily },
  tooltip: total === 0 ? { show: false } : {
    ...CHART_TOOLTIP,
    formatter: (p) => `<b>${escapeHtml(p.name)}</b><br/>Contracts: <b>${fmt(p.value)}</b> (${p.percent}%)`
  },
  graphic: [
    {
      type: 'text', left: 'center', top: 52, silent: true,
      style: { text: fmt(total), fontSize: 18, fontWeight: 'bold', fill: '#111827', fontFamily: CHART_FONT.fontFamily }
    },
    {
      type: 'text', left: 'center', top: 74, silent: true,
      style: { text: 'contracts', fontSize: 9, fill: '#6B7280', fontFamily: CHART_FONT.fontFamily }
    }
  ],
  series: [
    total === 0
      ? {
          type: 'pie', radius: [44, 66], silent: true, label: { show: false },
          data: [{ value: 1, name: '', itemStyle: { color: '#F3F4F6' } }]
        }
      : {
          type: 'pie',
          radius: [44, 66],
          label: { show: false },
          emphasis: { scale: false },
          data: items.map(item => ({
            value: item.contract_count || 0,
            name: item.type,
            itemStyle: { color: (SEGMENT_COLORS[item.type] || SEGMENT_COLORS.Other).fill }
          }))
        }
  ]
});

const ContractDomesticOverseaChart = ({ data, loading, error, onDrillDown }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading domestic/overseas data</p>
    </Card>
  );

  const items = data || [];
  const total = items.reduce((s, d) => s + (d.contract_count || 0), 0);

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <FiGlobe size={16} className="tw-text-emerald-500" />
        <h3 className="tw-font-bold tw-text-gray-900">Domestic vs Overseas</h3>
      </div>

      <div className="tw-p-5">
        {items.length === 0 ? (
          <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-6">
            <div className="tw-flex-shrink-0" style={{ width: 140, height: 140 }}>
              <ReactECharts
                option={buildDonutOption(items, total)}
                style={{ width: 140, height: 140 }}
                notMerge
                onEvents={{
                  click: (params) => {
                    const item = items[params.dataIndex];
                    if (onDrillDown && item && item.contract_count > 0) {
                      onDrillDown(`${item.type} Contracts`, { type_of_sales: item.type });
                    }
                  }
                }}
              />
            </div>

            <div className="tw-flex-1 tw-space-y-3">
              {items.map((item) => {
                const cfg       = SEGMENT_COLORS[item.type] || SEGMENT_COLORS.Other;
                const pct       = total > 0 ? ((item.contract_count / total) * 100).toFixed(1) : '0.0';
                const clickable = onDrillDown && item.contract_count > 0;
                return (
                  <div
                    key={item.type}
                    onClick={clickable ? () => onDrillDown(`${item.type} Contracts`, { type_of_sales: item.type }) : undefined}
                    className={`tw-rounded-lg tw-p-3 ${cfg.bg} tw-transition-all ${clickable ? 'tw-cursor-pointer hover:tw-shadow-md hover:tw-brightness-95' : ''}`}
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-w-2.5 tw-h-2.5 tw-rounded-xl ${cfg.badge}`} />
                        <span className={`tw-text-sm tw-font-bold ${cfg.text}`}>{item.type}</span>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-text-xs tw-font-semibold ${cfg.text}`}>{pct}%</span>
                        {clickable && <span className="tw-text-xs tw-text-gray-400">↗</span>}
                      </div>
                    </div>
                    <div className="tw-flex tw-items-end tw-justify-between">
                      <div>
                        <p className="tw-text-xl tw-font-extrabold tw-text-gray-900">{fmt(item.contract_count)}</p>
                        <p className="tw-text-xs tw-text-gray-500">contracts</p>
                      </div>
                      <div className="tw-text-right">
                        <p className={`tw-text-sm tw-font-bold ${cfg.text}`}>{fmtM(item.contract_value)}</p>
                        <p className="tw-text-xs tw-text-gray-400">{Number(item.contract_weight || 0).toFixed(1)} MT</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

ContractDomesticOverseaChart.propTypes = {
  data:        PropTypes.array,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  onDrillDown: PropTypes.func
};
ContractDomesticOverseaChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default ContractDomesticOverseaChart;
